import React from 'react';
import { Platform } from 'react-native';
import {
  TransparentModal,
  useBreakpointHelpers,
  useNoticeBannersStackContext,
  useWebKeyListener,
} from '@sweetgreen/garnish';

import { useAuthFlow } from '@order/AuthFlow';
import { useIsLoggedIn } from '@order/AuthMachine';
import { useLastInteractedStore } from '@order/LastInteractedStore';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { BagContents } from '../../components/Bag/Bag.contents';
import type { BagScreenProps } from '../../navigation/AppNavigation.props';

export const BagScreen = ({ navigation }: BagScreenProps) => {
  const isLoggedIn = useIsLoggedIn();
  const { startSignIn } = useAuthFlow();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const lastInteractedStore = useLastInteractedStore();
  const { track } = useTelemetry();

  // ––– HANDLERS ––––––––––––––––––––––––––––––––––––––––––––––––

  // – Navigate back

  const navigateBack = React.useCallback(() => {
    track('modal.close');

    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.getParent()?.goBack();
    }
  }, [navigation, track]);

  // – Navigate to Checkout (on pressing 'Check out' and successfully applying rewards)

  const navigateToCheckout = React.useCallback(() => {
    if (isLoggedIn) {
      navigation.navigate('Checkout');
    } else {
      startSignIn('checkout');
    }
  }, [navigation, isLoggedIn, startSignIn]);

  // – On press 'View Menu'

  const navigateToMenu = React.useCallback(() => {
    const { addressId, restaurantSlug } = lastInteractedStore ?? {};

    if (addressId) {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'DeliveryMenu',
          params: { addressId },
        },
      });
    } else if (restaurantSlug) {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'Menu',
          params: { restaurantSlug },
        },
      });
    } else {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'Location',
        },
      });
    }
  }, [navigation, lastInteractedStore]);

  const dismissAndNavigateToMenu = React.useCallback(() => {
    if (Platform.OS === 'web') {
      // Workaround for a bug in the web implementation that was causing the address bar to display
      // an incorrect URL when navigating from a modal to a screen contained in a stack nested within a tab.
      // It requires us to first dismiss the modal, then navigate after a timeout,
      // otherwise the calls would be debounced.
      navigation.getParent()?.goBack();

      setTimeout(() => {
        navigateToMenu();
      });
    } else {
      navigateToMenu();
    }
  }, [navigation, navigateToMenu]);

  // – Can change location? If signed out, start sign-in and redirect to Locations

  const canChangeLocation = React.useCallback(() => {
    if (isLoggedIn) return true;

    const text = t('bag.reorder.error-logged-out');

    addNoticeBanner({ text, palette: 'neutral' });
    startSignIn('locations');

    return false;
  }, [t, isLoggedIn, addNoticeBanner, startSignIn]);

  useWebKeyListener('Escape', navigateBack);

  const { isMobile } = useBreakpointHelpers();

  return (
    <TransparentModal
      size={isMobile ? 'full' : 'drawer'}
      onClose={navigateBack}
      mobileEdges={['right', 'left']}
    >
      <BagContents
        navigateToMenu={dismissAndNavigateToMenu}
        navigateToCheckout={navigateToCheckout}
        canChangeLocation={canChangeLocation}
      />
    </TransparentModal>
  );
};
